import { INTEGRATIONS } from "@/shared/data";
import { api } from "./api";
import { staticAssetsApi } from "./static-assets";

const path = (path: string) => `/integrations${path}`;

export const integrationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrations: builder.query({
      query: () => path(""),
      providesTags: ["integrations"],
    }),
    getCRMIntegrations: builder.query({
      query: () => path(""),
      providesTags: ["integrations"],
      transformResponse: (response: any) => {
        const allIntegrations = response.map((integration: any) => {
          integration.metadata = INTEGRATIONS.find(
            (i: any) => i.internalName === integration.vendor,
          );

          return integration;
        });

        if (allIntegrations) {
          return allIntegrations.filter(
            (item: any) => item.metadata?.type === "CRM" && item.active,
          );
        }

        return [];
      },
    }),
    updateIntegration: builder.mutation({
      query: ({ id, data }) => ({
        url: path(`/${id}`),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["integrations"],
    }),
    deleteIntegration: builder.mutation({
      query: ({ vendor }) => ({
        url: path(`/${vendor}`),
        method: "DELETE",
      }),
      invalidatesTags: ["integrations"],
    }),
    exchangeCode: builder.mutation({
      query: ({
        vendor,
        code,
        isSandbox = false,
        state,
      }: {
        vendor: string;
        code: string;
        isSandbox?: boolean;
        state?: string;
      }) => ({
        url: path(`/${vendor}/code`),
        method: "POST",
        body: { code, isSandbox, state },
      }),
      invalidatesTags: ["integrations"],
    }),
    getConnectionLink: builder.query({
      query: ({
        vendor,
        isSandbox = false,
      }: {
        vendor: string;
        isSandbox?: boolean;
      }) => {
        const params: Record<string, string | boolean> = {};
        if (isSandbox) params.isSandbox = isSandbox;
        return {
          url: path(`/${vendor}/connectionLink`),
          params,
        };
      },
    }),
    getContacts: builder.mutation({
      query: ({ vendor, searchTerm }) => ({
        url: path(`/${vendor}/contacts`),
        method: "POST",
        body: { searchTerm },
      }),
    }),
    getContact: builder.query({
      query: ({ vendor, id }) => path(`/${vendor}/contact/${id}`),
    }),
    getCustom: builder.mutation({
      query: ({ vendor, searchTerm }) => ({
        url: path(`/${vendor}/custom`),
        method: "POST",
        body: { searchTerm },
      }),
    }),
    getCustomData: builder.mutation({
      query: ({ vendor, contact }) => ({
        url: path(`/${vendor}/custom/data`),
        method: "POST",
        body: contact,
      }),
    }),
    updateAccountIntegrations: builder.mutation({
      query: ({
        id,
        code,
        enabled,
        metadata,
      }: {
        id: string;
        code: string;
        enabled: boolean;
        accountId: string;
        metadata: Record<string, unknown>;
      }) => ({
        url: path("/marketing"),
        method: "PUT",
        body: { code, enabled, id, metadata },
      }),
      onQueryStarted: async ({ accountId }, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;

          const patchResult = dispatch(
            staticAssetsApi.util.updateQueryData(
              "getAccountIntegrations",
              { id: accountId },
              () => data,
            ),
          );

          if (!patchResult.patches.length) {
            dispatch(
              staticAssetsApi.util.upsertQueryData(
                "getAccountIntegrations",
                { id: accountId },
                data,
              ),
            );
          }
        } catch (error) {
          console.error("Failed to update account integrations cache:", error);
        }
      },
    }),
    getSalesforceSObjectDescribe: builder.query<
      Array<{
        name: string;
        label: string;
        type: string;
        referenceTo: string[];
        relationshipName: string | null;
      }>,
      { name: string }
    >({
      query: ({ name }) => path(`/salesforce/sobject/${name}/describe`),
    }),
    getHubspotProperties: builder.query<
      Array<{ name: string; label: string; type: string; fieldType: string; groupName: string }>,
      { objectType: string }
    >({
      query: ({ objectType }) => path(`/hubspot/properties/${objectType}`),
    }),
    getHubspotEntity: builder.mutation<
      Record<string, Record<string, unknown>>,
      { objectType: string; id: string; properties: Record<string, string[]> }
    >({
      query: ({ objectType, id, properties }) => ({
        url: path(`/hubspot/entity/${objectType}/${id}`),
        method: "POST",
        body: { properties },
      }),
    }),
    searchHubspotEntities: builder.mutation<
      Array<{ id: string; name: string; objectType: "contact" | "deal" | "company" }>,
      { searchTerm: string; objectTypes?: ("contact" | "deal" | "company")[] }
    >({
      query: ({ searchTerm, objectTypes }) => ({
        url: path("/hubspot/search"),
        method: "POST",
        body: { searchTerm, objectTypes: objectTypes ?? ["contact", "deal", "company"] },
      }),
    }),
    getFathomMeetings: builder.query<
      {
        items: Array<{
          recordingId: string;
          shareUrl: string;
          title: string;
          date: string;
        }>;
        nextCursor: string | null;
      },
      { cursor?: string }
    >({
      query: ({ cursor } = {}) => ({
        url: path("/fathom/meetings"),
        method: "GET",
        params: cursor ? { cursor } : {},
      }),
    }),
    fillFathomMeetingVariables: builder.mutation<
      { variables: Array<{ name: string; response: string }> },
      {
        shareUrl?: string;
        recordingId?: string;
        variables: Array<{ name: string }>;
      }
    >({
      query: ({ shareUrl, recordingId, variables }) => ({
        url: path("/fathom/meeting-variables"),
        method: "POST",
        body: { ...(recordingId ? { recordingId } : { shareUrl }), variables },
      }),
    }),
    // Slack (Storydoc Assistant) — org-level install state. Unlike the CRM
    // connectors this isn't in the generic integrations collection; it reflects
    // the workspace install bound to the org via storydoc_org_id.
    getSlackStatus: builder.query<
      {
        installed: boolean;
        workspaces: Array<{
          teamId: string;
          teamName: string | null;
          connectedAt: string;
        }>;
      },
      void
    >({
      query: () => path("/slack/status"),
      providesTags: ["slack-integration"],
    }),
    disconnectSlack: builder.mutation<
      {
        installed: boolean;
        workspaces: Array<{
          teamId: string;
          teamName: string | null;
          connectedAt: string;
        }>;
      },
      { teamId: string }
    >({
      query: ({ teamId }) => ({
        url: path("/slack/disconnect"),
        method: "POST",
        body: { teamId },
      }),
      invalidatesTags: ["slack-integration"],
    }),
  }),
});

export const {
  useGetCRMIntegrationsQuery,
  useLazyGetCRMIntegrationsQuery,
  useGetContactsMutation,
  useGetContactQuery,
  useLazyGetContactQuery,
  useGetCustomMutation,
  useGetCustomDataMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useExchangeCodeMutation,
  useLazyGetConnectionLinkQuery,
  useGetIntegrationsQuery,
  useUpdateAccountIntegrationsMutation,
  useLazyGetSalesforceSObjectDescribeQuery,
  useGetSalesforceSObjectDescribeQuery,
  useLazyGetHubspotPropertiesQuery,
  useGetHubspotPropertiesQuery,
  useGetHubspotEntityMutation,
  useSearchHubspotEntitiesMutation,
  useFillFathomMeetingVariablesMutation,
  useLazyGetFathomMeetingsQuery,
  useGetSlackStatusQuery,
  useDisconnectSlackMutation,
} = integrationsApi;
